import type { NextPage } from "next";
import dynamic from "next/dynamic";
import { FAQPage, WithContext } from "schema-dts";

import { getModifiedDate } from "lib/helpers";
import { siteDataQuery } from "lib/sanity/queries";
import { getClient } from "lib/sanity/server";

import BiggestNamesInSolar from "components/biggest-names-in-solar";
import Page from "components/layout/page";
import { fallbackData, SiteData } from "components/views/constants";
import FaqSection from "components/views/home/faqs/faqs";
import FeatureSections from "components/views/home/features";
import FeaturesOverview from "components/views/home/features-overview";
import FinancingMarketIntel from "components/views/home/financing-market-intel";
import GrewFast from "components/views/home/grew-fast";
import HowOSWorks from "components/views/home/how-os-works";
import HomeFAQJsonLD from "components/views/home/json-ld";
import JumpTo from "components/views/home/jump-to";
import Solutions from "components/views/home/solutions";
import Stats from "components/views/home/stats";

const DemoFormSection = dynamic(() => import("../components/demo-form-section"));
const MainHero = dynamic(() => import("../components/views/home/main-hero"));

type HomePageProps = { siteData: SiteData; jsonLd: WithContext<FAQPage> };

const Home: NextPage<HomePageProps> = ({ siteData, jsonLd }: HomePageProps) => {
  return (
    <Page
      title="The Operating System for Residential Solar"
      description="Enerflo is the operating system for residential solar — connecting your solar CRM, design tools, financing, contracting, and project management into one infrastructure layer. From first knock to PTO. Trusted by leading residential solar companies."
    >
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <MainHero />
      <JumpTo />
      <GrewFast />
      <HowOSWorks />
      <Stats revenueToDate={siteData.revenueToDate} />
      <FeaturesOverview />
      <FeatureSections />
      <Solutions />
      <FinancingMarketIntel />
      <FaqSection />
      <BiggestNamesInSolar />
      <DemoFormSection />
    </Page>
  );
};

export default Home;

export async function getStaticProps(ctx) {
  const client = getClient(ctx.preview);
  let siteData = fallbackData;

  try {
    const data = await client.fetch(siteDataQuery);

    if (data) {
      siteData = data;
    }
  } catch (e) {
    console.error("Unable to fetch site data", e);
  }

  const jsonLd: WithContext<FAQPage> = {
    ...HomeFAQJsonLD,
    dateModified: getModifiedDate(),
  };

  return {
    props: {
      siteData,
      jsonLd,
    },
  };
}
